import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
//import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import { Autocomplete } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';

import compose from 'helpers/compose';
import ControlButtons from 'components/ControlButtons';
import VariantSelect from 'components/VariantSelect/VariantSelect';

import { EditAccountStore } from 'stores/editAccountStore';
import { ACCOUNTS_EDIT_FINANCE, ACCOUNTS_EDIT_INVESTMENT, ACCOUNTS_EDIT_BASIC, Params } from '../accountEditRoutes';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import { AccountMode, PersonType } from 'enums';
import { RootStore } from 'stores/rootStore';
import { LinkableAccount } from 'pages/Accounts/Edit/components/ExternalProducts/model';
import { EdwardColors } from 'enums';
import { searchInStrings } from 'helpers/helpers';

interface Props {
  editAccountStore?: EditAccountStore;
  rootStore?: RootStore;
}

const VariantSelection: React.FC<Props> = ({ editAccountStore, rootStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const history = useHistory();
  const [accountsOpen, setAccountsOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [value, setValue] = useState<LinkableAccount | null>(null);
  const [id, setId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      editAccountStore!.setStep(1);
    })();
  }, [editAccountStore, accountId]);

  const setMode = async (mode: AccountMode, route: string) => {
    const ok = await editAccountStore!.setVariant(mode);
    if (ok) history.push(route);
  };

  const findAccounts = async () => {
    await editAccountStore!.getAccounts();
    setAccountsOpen(true);
  };

  return (
    <>
      <VariantSelect
        title="variantSelection_title"
        variants={[
          {
            title: 'variantSelection_withInvestment',
            text:
              editAccountStore?.mode === AccountMode.LTIP
                ? 'variantSelection_LTIP_selected'
                : 'variantSelection_withInvestmentText',
            action: () => setMode(AccountMode.STANDARD, ACCOUNTS_EDIT_FINANCE(accountId)),
            disabled: editAccountStore?.mode === null || editAccountStore?.mode === AccountMode.LTIP,
          },
          {
            title: 'variantSelection_portfolioOnly',
            text:
              editAccountStore?.mode === AccountMode.LTIP
                ? 'variantSelection_LTIP_selected'
                : 'variantSelection_portfolioText',
            action: () => setMode(AccountMode.STANDARD, ACCOUNTS_EDIT_INVESTMENT(accountId)),
            disabled: editAccountStore?.mode === null || editAccountStore?.mode === AccountMode.LTIP,
          },
          {
            title: 'variantSelection_LIP',
            text:
              editAccountStore?.mode === AccountMode.STANDARD
                ? 'variantSelection_STD_selected'
                : 'variantSelection_LIPText',
            action: () => setMode(AccountMode.LTIP, ACCOUNTS_EDIT_FINANCE(accountId)),
            disabled: editAccountStore?.mode === null || editAccountStore?.mode === AccountMode.STANDARD,
            beta: true,
            hidden: editAccountStore?.data?.client.personType === PersonType.LEGAL,
          },
        ]}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileCopyOutlinedIcon fontSize="inherit" />}
          onClick={findAccounts}
          disabled={!!editAccountStore?.mode}
        >
          {t('variantSelection_clone')}
        </Button>
      </VariantSelect>

      <ConfirmDialog
        title={t('variantSelection_clone')}
        isOpen={accountsOpen}
        confirm={async () => {
          if (id) {
            const ok = await editAccountStore!.linkAccount(id);
            if (ok) {
              setAccountsOpen(false);
              await editAccountStore!.fetchBasicData(accountId);
              history.push(ACCOUNTS_EDIT_FINANCE(accountId));
            }
          }
        }}
        close={() => {
          setAccountsOpen(false);
        }}
        btnCloseText={'general_cancel'}
        btnConfirmText={'variantSelection_copy'}
        icons
        confirmDisabled={!id}
      >
        <Typography>{t('variantSelection_clone_info')}</Typography>
        <div style={{ padding: '16px 0' }}>
          {!editAccountStore!.accounts || !editAccountStore!.accounts.length ? (
            <Typography variant="h5">{t('variantSelection_clone_noAccounts')}</Typography>
          ) : (
            <>
              {/*
            <TextField
              select={true}
              defaultValue={''}
              onChange={(e) => setLinkedAccount(e.target.value)} //store.switchAccount(e.target.value)
              //className={classes.accountSelection}
              label={t('variantSelection_clone_account')}
            >
              {editAccountStore!.accounts.map((account) => (
                <MenuItem key={account.accountId} value={account.accountId}>
                  {account.title}
                  {account.variableSymbol && ` (${t('backOffice_advisors_variableSymbol')}: ${account.variableSymbol})`}
                </MenuItem>
              ))}
            </TextField>
            */}

              <Autocomplete
                open={open}
                onOpen={() => {
                  setOpen(true);
                }}
                onClose={() => {
                  setOpen(false);
                }}
                value={value}
                onChange={(event: any, newValue: LinkableAccount | null) => {
                  setValue(newValue);
                  setId(newValue ? newValue.accountId : null);
                  setSearch(newValue ? `${newValue.title} (${newValue.clientName})` : '');
                }}
                getOptionSelected={(option, value) => option.title === value.title}
                getOptionLabel={(option) => `${option.title} (${option.clientName})`}
                options={editAccountStore!.accounts} //
                inputValue={search}
                filterOptions={(options: LinkableAccount[], state: object) =>
                  options.filter((o) => searchInStrings(search.trim(), [o.title, o.clientName || '']))
                }
                clearOnBlur={false}
                noOptionsText={t('general_noResults')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(`variantSelection_clone_account`)}
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon htmlColor={EdwardColors.GRAY_LIGHTER} />,
                    }}
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                    }}
                    //className={classes.input}
                  />
                )}
              />
            </>
          )}
        </div>
      </ConfirmDialog>

      <ControlButtons
        isSubmitting={false}
        backText={t('accountStepper_basic')}
        hideNext={true}
        onBack={() => history.push(ACCOUNTS_EDIT_BASIC(accountId))}
      />
    </>
  );
};

export default compose(inject('editAccountStore', 'rootStore'), observer)(VariantSelection);
